import type {
  CreateDestinationPayload,
  DestinationListParams,
  UpdateDestinationPayload,
} from '@/shared/api/types/destination-types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DASHBOARD_QUERY_KEYS } from '@/entities/dashboard'
import { POI_QUERY_KEYS } from '@/entities/poi/api/poi-queries'
import { TENANT_DESTINATION_QUERY_KEYS } from '@/entities/tenant-destination/api/tenant-destination-queries'
import { TOUR_QUERY_KEYS } from '@/entities/tour/api/tour-queries'
import {
  createDestination,
  deleteDestination,
  getDestinationById,
  listDestinations,
  updateDestination,
} from '@/shared/api/services/destination-service'

export const DESTINATION_QUERY_KEYS = {
  all: ['destinations'] as const,
  listPrefix: () => [...DESTINATION_QUERY_KEYS.all, 'list'] as const,
  list: (params: DestinationListParams) => [...DESTINATION_QUERY_KEYS.all, 'list', params] as const,
  detailPrefix: (id: string) => [...DESTINATION_QUERY_KEYS.all, 'detail', id] as const,
  detail: (id: string) => [...DESTINATION_QUERY_KEYS.all, 'detail', id] as const,
}

export function useDestinationsQuery(
  params: DestinationListParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: DESTINATION_QUERY_KEYS.list(params),
    queryFn: async () => listDestinations(params),
    enabled: options?.enabled ?? true,
  })
}

export function useDestinationByIdQuery(id: string) {
  return useQuery({
    queryKey: DESTINATION_QUERY_KEYS.detail(id),
    queryFn: async () => getDestinationById(id),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

async function invalidateDependentQueries(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: TENANT_DESTINATION_QUERY_KEYS.all }),
    queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.all }),
    queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.all }),
    queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.all }),
  ])
}

export function useCreateDestinationMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateDestinationPayload) => createDestination(payload),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: DESTINATION_QUERY_KEYS.listPrefix() })
      await queryClient.invalidateQueries({
        queryKey: DESTINATION_QUERY_KEYS.detailPrefix(response.data.id),
      })
      await invalidateDependentQueries(queryClient)
    },
  })
}

export function useUpdateDestinationMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateDestinationPayload }) =>
      updateDestination(id, payload),
    onSuccess: async (_response, { id }) => {
      await queryClient.invalidateQueries({ queryKey: DESTINATION_QUERY_KEYS.listPrefix() })
      await queryClient.invalidateQueries({ queryKey: DESTINATION_QUERY_KEYS.detailPrefix(id) })
      await queryClient.invalidateQueries({ queryKey: TENANT_DESTINATION_QUERY_KEYS.all })
      await invalidateDependentQueries(queryClient)
    },
  })
}

export function useDeleteDestinationMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => deleteDestination(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: DESTINATION_QUERY_KEYS.listPrefix() })
      await invalidateDependentQueries(queryClient)
    },
  })
}
